import React, { useCallback } from "react";
import {
  Chart,
  ChartTitle,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartXAxis,
  ChartXAxisItem,
  ChartYAxis,
  ChartYAxisItem,
  ChartArea,
  ChartTooltip,
  SeriesNotes,
} from "@progress/kendo-react-charts";
import "hammerjs";

interface WellSurveyPlot1Props {
  selectedRow: any;
  surveyData: any[];
  maxDls?: number;
  maxMd?: number;
  customerName?: string;
  wellName?: string;
  surveyMetaData?: {
    kop: number;
    topOfEspPump: number;
    bottomOfESPSensor: number;
    tailPipeDesander: number;
    tailPipeBottom: number;
  };
}

const notesConfig: SeriesNotes = {
  label: {
    position: "outside",
    background: "gray",
    color: "white",
    border: {
      color: "gray",
      width: 5,
      dashType: "solid",
    },
  },
  line: {
    color: "gray",
    length: 75,
  },
  icon: {
    type: "circle",
  },
  position: "right",
};

const WellSurveyPlot1: React.FC<WellSurveyPlot1Props> = ({
  selectedRow,
  surveyData,
  maxDls,
  maxMd,
  customerName = "--",
  wellName = "--",
  surveyMetaData,
}) => {
  const kopIndex = surveyMetaData ? surveyData.findIndex(x => x.md === +(surveyMetaData.kop)) : -1;
  const surveyLength = surveyData.length - 1;
  let start = 0;
  let end = surveyData.length >= 28 ? 27 : surveyLength;
  if (kopIndex >= 0) {
    start = kopIndex >= 18 ? kopIndex - 18 : 0;
    end = (surveyLength - kopIndex) >= 8 ? kopIndex + 8 : surveyLength;
  }
  const kopPlotData = surveyData.slice(start, end);

  const getKOPData = useCallback(() => {
    if ((surveyMetaData?.kop ?? 0) > 0 && surveyData) {
      const kopIndex = kopPlotData.findIndex((x) => x.md === +(surveyMetaData?.kop ?? 0));
      if (kopIndex > 0) {
        return [kopPlotData[kopIndex], kopPlotData[kopIndex - 1]];
      }
    }
    return null;
  }, [kopPlotData, surveyMetaData]);

  const kopData = getKOPData();
  const kopMaxDls = Math.max(...kopPlotData.map(d => d.dls));
  const kopMaxMd = Math.max(...kopPlotData.map(d => d.md));
  const minMd = kopPlotData?.[0]?.md;

  const getTubingData = useCallback(() => {
    if (surveyMetaData && typeof surveyMetaData.topOfEspPump === 'number' && surveyMetaData.topOfEspPump > 0) {
      return [
        { dls: 2, md: 0 },
        { dls: 2, md: surveyMetaData.topOfEspPump }
      ];
    }
    return [];
  }, [surveyMetaData]);

  return (
    <Chart
      zoomable={{
        mousewheel: {
          lock: "x",
          rate: 0.03,
        },
        selection: {
          lock: "x",
        },
      }}
      className='height-max flex justify-center items-start p-4 gap-4 w-full'
    >
      <ChartArea
        background='transparent'
        height={900}
        width={900}
        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
      />

      <ChartTitle
        text={`Customer\t\t:\t${customerName}\nWellName\t\t:\t${wellName}`}
        color='#fff'
        font='18px Mulish'
        align='left'
      />

      <ChartLegend
        position='bottom'
        labels={{
          font: "16px Mulish",
          color: "#fff",
        }}
      />

      <ChartSeries>
        <ChartSeriesItem
          type='scatterLine'
          data={kopPlotData}
          xField='inc'
          yField='md'
          name='INC'
          color='#f97316'
          style='smooth'
          markers={{
            type: "cross",
            size: 6,
            background: "#f97316",
            visible: true,
          }}
          tooltip={{
            border: {
              width: 0,
              color: "transparent",
            },
            visible: true,
            format: "INC: {0}, MD: {1}",
          }}
          width={2}
        />

        <ChartSeriesItem
          type='scatterLine'
          data={kopPlotData}
          xAxis="DLS"
          xField='dls'
          yField='md'
          name='DLS'
          color='#204887'
          style='smooth'
          markers={{
            type: "circle",
            size: 8,
            visible: true,
          }}
          tooltip={{
            visible: true,
            format: "DLS: {0}, MD: {1}",
          }}
          width={2}
          noteTextField="note"
          notes={notesConfig}
        />

        {kopData && kopData[0] && (
          <ChartSeriesItem
            type='scatterLine'
            data={[kopData[0]]}
            xField='dls'
            yField='md'
            color='#204887'
            dashType='solid'
            xAxis='DLS'
            markers={{
              type: "circle",
              size: 8,
              background: "#204887",
              visible: true,
            }}
            tooltip={{
              visible: true,
              format: "DLS: {0}, MD: {1}",
            }}
            width={2}
            noteTextField="note"
            visibleInLegend={false}
          />
        )}

        {kopData && kopData[1] && (
          <ChartSeriesItem
            type='scatterLine'
            data={[kopData[1]]}
            xField='dls'
            yField='md'
            color='#204887'
            dashType='solid'
            xAxis='DLS'
            markers={{
              type: "circle",
              size: 8,
              background: "#204887",
              visible: true,
            }}
            tooltip={{
              visible: true,
              format: "DLS: {0}, MD: {1}",
            }}
            width={2}
            noteTextField="note"
            visibleInLegend={false}
          />
        )}

        {surveyMetaData && surveyMetaData.topOfEspPump > 0 && (
          <ChartSeriesItem
            type="scatterLine"
            data={getTubingData()}
            xField="dls"
            yField="md"
            name="Tubing"
            xAxis="DLS"
            color="#36454F"
            width={5}
            markers={{
              visible: false
            }}
            tooltip={{
              visible: true,
              format: "Tubing: {0}, MD: {1}"
            }}
            style="smooth"
          />
        )}

        {surveyMetaData &&
          surveyMetaData?.topOfEspPump > 0 &&
          surveyMetaData?.bottomOfESPSensor > 0 && (
            <ChartSeriesItem
              type='scatterLine'
              data={[
                { dls: 2, md: surveyMetaData?.topOfEspPump },
                { dls: 2, md: surveyMetaData?.bottomOfESPSensor },
              ]}
              xField='dls'
              yField='md'
              name='ESP'
              color='#DC4405'
              width={8}
              xAxis='DLS'
              markers={{
                visible: false,
              }}
              tooltip={{
                border: {
                  width: 0,
                },
                visible: true,
                format: "ESP: {0}, MD: {1}",
              }}
              dashType='solid'
              noteTextField='note'
              notes={{
                ...notesConfig,
                position: "right",
                icon: { type: "square" },
              }}
            />
          )}

        {surveyMetaData &&
          surveyMetaData?.bottomOfESPSensor > 0 &&
          surveyMetaData?.tailPipeBottom > 0 && (
            <ChartSeriesItem
              type='scatterLine'
              data={[
                { dls: 2, md: surveyMetaData?.bottomOfESPSensor },
                { dls: 2, md: surveyMetaData?.tailPipeBottom },
              ]}
              xField='dls'
              yField='md'
              name='TailPipe'
              color='#059bb4'
              width={2}
              xAxis='DLS'
              markers={{
                visible: false,
              }}
              tooltip={{
                border: {
                  width: 0,
                  color: "transparent",
                },
                visible: true,
                format: "TailPipe: {0}, MD: {1}",
              }}
              dashType='dash'
              noteTextField='note'
              notes={notesConfig}
            />
          )}
      </ChartSeries>

      <ChartYAxis>
        <ChartYAxisItem
          title={{
            text: "MD",
            font: "16px Mulish",
            color: "#fff",
          }}
          color='#fff'
          min={minMd}
          max={kopMaxMd}
          axisCrossingValue={[0, kopMaxMd]}
          reverse={true}
          majorGridLines={{
            visible: true,
            width: 0.75,
            color: "#A0A0A0",
          }}
        />
      </ChartYAxis>

      <ChartXAxis>
        <ChartXAxisItem
          name="INC"
          title={{
            text: "INC",
            font: "bold 16px Verdana, sans-serif",
            color: "#fff",
            margin: 20
          }}
          axisCrossingValue={[0]}
          reverse={false}
          color="#fff"
          majorGridLines={{
            visible: true,
            width: 0.75,
            color: "#A0A0A0"
          }}
          minorGridLines={{
            width: 0.75,
            color: "#E6E6E6",
            visible: false
          }}
        />

        <ChartXAxisItem
          name='DLS'
          title={{
            text: "DLS",
            font: "bold 16px Verdana, sans-serif",
            color: "#fff",
            margin: 20,
          }}
          color='#fff'
          min={0}
          max={kopMaxDls + 2}
          axisCrossingValue={[0]}
          reverse={false}
          majorGridLines={{
            visible: true,
            width: 0.75,
            color: "#A0A0A0",
          }}
          minorGridLines={{
            visible: false,
            width: 0.75,
            color: "#E6E6E6",
          }}
        />
      </ChartXAxis>

      <ChartTooltip
        shared={true}
        background='#1E293B'
        color='#fff'
        font='14px Mulish'
      />
    </Chart>
  );
};

export default WellSurveyPlot1;